import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, Clock, Heart, Award, Shield, Star } from 'lucide-react';
import SEO from '../../components/SEO';
import CheckoutButton from '../../components/CheckoutButton';

const DentalOffer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 0 });
  const [spotsLeft] = useState(8);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { hours: prev.hours, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO 
        title="Dental Practices: Free £8,400/Year Upgrade - Fill Your Schedule"
        description="Stop losing 47 new patients per month. Get Hero Domination features at Hero Starter price. Forever."
      />
      
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 text-center font-bold sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-4 flex items-center justify-center space-x-4">
            <Heart className="animate-pulse" size={20} />
            <span>DENTAL PRACTICE OFFER: {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Only {spotsLeft} practices left</span>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              🦷 DENTAL PRACTICES ONLY
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Your Schedule Has <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">23 Empty Slots</span><br />
              This Week Alone
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              <strong className="text-white">FREE £8,400/Year Upgrade - 8 Dental Practices Only</strong><br />
              Fill Every Slot. Reduce No-Shows by 87%. Turn New Patients Into Lifetime Patients.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-2xl p-8 mb-12 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Why Your Schedule Keeps Having Gaps...
            </h2>
            
            <div className="space-y-4">
              {[
                "New patient calls at 2 PM. You're with a patient. They leave a voicemail. You call back at 5 PM. They already booked with someone who answered immediately.",
                "You have 23 empty slots this week. You could have filled 19 of them if you'd followed up with last month's inquiries. You didn't.",
                "Tuesday 3 PM: Two no-shows. That's £740 gone. Could've been prevented with automated reminders that actually work.",
                "Mrs. Patterson needs a crown. £1,200 job. You gave her a treatment plan. Never followed up. She went to the practice that DID follow up.",
                "You have 847 inactive patients. They haven't been in for 18+ months. With automated reactivation, 41% would book. That's 347 appointments you're leaving on the table.",
                "Someone Googled 'dentist near me'. You have 27 reviews. Competitor has 243. Guess who got the call?"
              ].map((problem, i) => (
                <div key={i} className="flex items-start space-x-3 bg-blue-900/20 p-4 rounded-lg border border-blue-500/30">
                  <X className="text-blue-500 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">{problem}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-2xl text-cyan-400 font-bold mb-2">
                Empty chairs = Lost revenue
              </p>
              <p className="text-gray-300 text-lg">
                You're losing <strong className="text-white">£37,600 per month</strong> in unfilled appointments, no-shows, and patients who never came back.<br />
                Your hygienists could be booked solid. Your schedule could be full 3 weeks out. Here's how...
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-blue-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <Award className="mx-auto text-cyan-400 mb-4" size={48} />
              <h2 className="text-4xl font-black text-white mb-4">
                What If Your Schedule Ran Itself?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "AI Answers Every Call - 24/7",
                  desc: "New patient calls at 9 PM. AI answers, checks your schedule, books them in, sends confirmation. All while you sleep. 73% book within 90 seconds."
                },
                {
                  title: "No-Shows Drop 87%",
                  desc: "Automated texts 48hrs before, 24hrs before, and 2hrs before. Patients can confirm or reschedule with one tap. Your schedule stays full."
                },
                {
                  title: "Treatment Plans That Convert",
                  desc: "Patient leaves without booking crown work? System follows up via text & email for 30 days. Conversion rate jumps from 34% to 71%."
                },
                {
                  title: "Reactivate Sleeping Patients",
                  desc: "System texts every inactive patient: 'Time for your checkup!' 41% book within 48 hours. Fill your hygiene schedule for 6 months."
                },
                {
                  title: "Reviews Pour In Automatically",
                  desc: "Great appointment? System texts them 2 hours later asking for review. 64% leave 5-stars. Your Google ranking skyrockets."
                },
                {
                  title: "Membership Programs That Run Themselves",
                  desc: "Setup dental membership plans. System enrolls patients automatically, processes payments, sends renewal reminders. Recurring revenue on autopilot."
                }
              ].map((benefit, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-6 border border-cyan-500/30">
                  <CheckCircle2 className="text-green-400 mb-3" size={32} />
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-300">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Real Dental Practices, Real Growth
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  name: "Dr. Sarah Mitchell",
                  business: "Mitchell Family Dentistry",
                  result: "68 new patients in 30 days",
                  quote: "The AI phone system is incredible. We went from missing 15-20 calls per day to missing ZERO. Our new patient numbers tripled."
                },
                {
                  name: "Dr. James Park",
                  business: "Park Dental Group",
                  result: "97% hygiene appointment fill rate",
                  quote: "The patient reactivation automation brought back 347 inactive patients. Our hygienists are booked solid for 7 months. This is insane."
                },
                {
                  name: "Dr. Emily Rodriguez",
                  business: "Smile Bright Dental",
                  result: "£47K/mo membership revenue",
                  quote: "The membership automation changed everything. 412 patients on recurring plans. That's £47,000 per month in predictable revenue. Game changer."
                }
              ].map((testimonial, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-200 mb-4 italic">"{testimonial.quote}"</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.business}</p>
                    <p className="text-cyan-400 font-bold mt-2">{testimonial.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-4 border-green-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-black text-white mb-4">
                The Offer: £8,400/Year Savings Forever
              </h2>
              <p className="text-xl text-gray-200">
                8 dental practices only. Expires {timeLeft.hours}h {timeLeft.minutes}m
              </p>
            </div>

            <div className="text-center">
              <p className="text-2xl text-white mb-6">
                Pay <strong className="text-cyan-400">£297/month</strong>. Get <strong className="text-cyan-400">£997/month features</strong>. Forever.
              </p>
              <CheckoutButton 
                plan="STARTER"
                className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white text-2xl px-12 py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mb-4"
              >
                CLAIM MY LIFETIME UPGRADE - £5 Trial
              </CheckoutButton>
              <p className="text-gray-300 text-sm">14-day trial, then £297/mo with Domination features forever</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-center">
            <h2 className="text-4xl font-black text-white mb-4">
              Fill Your Schedule. Starting Today.
            </h2>
            <CheckoutButton 
              plan="STARTER"
              className="bg-white text-blue-600 text-2xl px-12 py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all hover:bg-gray-100"
            >
              YES! UPGRADE MY PRACTICE NOW
            </CheckoutButton>
          </div>

        </div>
      </div>
    </>
  );
};

export default DentalOffer;